import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  createStage,
  updateStage,
  addStageComment,
  fetchStageTimeline,
  fetchStageComments,
  fetchStageTypesByOrganisation,
} from "../Api/StageMaster";
import moment from "moment";

const StageModal = ({
  show,
  onHide,
  stage,
  orgId,
  projectId,
  onStageSaved,
}) => {
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId || "";

  const [formData, setFormData] = useState({
    stageName: "",
    stageDescription: "",
    stageProjectBelong: "",
    orgId: "",
    stageStartDateTime: "",
    stageEndDateTime: "",
    stageType: "",
    createdBy: "",
    active: true,
  });

  const [activeTab, setActiveTab] = useState("details");
  const [timeline, setTimeline] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // State for stage types from API
  const [stageTypes, setStageTypes] = useState([]);
  const [stageTypesLoading, setStageTypesLoading] = useState(false);
  const [stageTypesError, setStageTypesError] = useState(null);

  // Fetch stage types from API when modal opens and orgId is available
  useEffect(() => {
    const loadStageTypes = async () => {
      if (!orgId) {
        console.log("No orgId provided, skipping stage types fetch");
        return;
      }

      setStageTypesLoading(true);
      setStageTypesError(null);
      try {
        const types = await fetchStageTypesByOrganisation(orgId);
        console.log("Fetched stage types:", types);
        setStageTypes(types || []);
      } catch (error) {
        console.error("Error loading stage types:", error);
        setStageTypesError("Failed to load stage types");
        setStageTypes([]);
      } finally {
        setStageTypesLoading(false);
      }
    };

    if (show && orgId) {
      loadStageTypes();
    }
  }, [show, orgId]);

  useEffect(() => {
    if (stage) {
      // Editing existing stage
      setFormData({
        stageName: stage.stageName || "",
        stageDescription: stage.stageDescription || "",
        stageProjectBelong: stage.stageProjectBelong || projectId,
        orgId: stage.orgId || orgId,
        stageStartDateTime: stage.stageStartDateTime
          ? moment(stage.stageStartDateTime).format("YYYY-MM-DDTHH:mm")
          : "",
        stageEndDateTime: stage.stageEndDateTime
          ? moment(stage.stageEndDateTime).format("YYYY-MM-DDTHH:mm")
          : "",
        stageType: stage.stageType || "",
        createdBy: stage.createdBy || userId,
        active: stage.active !== undefined ? stage.active : true,
      });
    } else {
      // Creating new stage
      setFormData({
        stageName: "",
        stageDescription: "",
        stageProjectBelong: projectId,
        orgId: String(orgId),
        stageStartDateTime: "",
        stageEndDateTime: "",
        stageType: "",
        createdBy: userId,
        active: true,
      });
    }

    // Reset errors and active tab when modal opens
    setErrors({});
    setActiveTab("details");
  }, [stage, projectId, orgId, show]);

  // Load timeline when stage exists and activity tab is active
  useEffect(() => {
    if (stage && show && activeTab === "activity") {
      loadTimeline();
    }
  }, [stage, show, activeTab]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.stageName.trim()) {
      newErrors.stageName = "Stage name is required";
    }

    if (!formData.stageStartDateTime) {
      newErrors.stageStartDateTime = "Stage start date is required";
    }

    if (!formData.stageEndDateTime) {
      newErrors.stageEndDateTime = "Stage end date is required";
    }

    if (formData.stageStartDateTime && formData.stageEndDateTime) {
      const startDate = new Date(formData.stageStartDateTime);
      const endDate = new Date(formData.stageEndDateTime);

      if (endDate <= startDate) {
        newErrors.stageEndDateTime = "Stage end date must be after start date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        stageName: formData.stageName,
        stageDescription: formData.stageDescription,
        stageProjectBelong: formData.stageProjectBelong,
        orgId: String(formData.orgId),
        stageStartDateTime: formData.stageStartDateTime
          ? new Date(formData.stageStartDateTime).toISOString()
          : null,
        stageEndDateTime: formData.stageEndDateTime
          ? new Date(formData.stageEndDateTime).toISOString()
          : null,
        stageType: formData.stageType,
      };

      if (stage) {
        // Update existing stage - wrap in updateDto object
        const updatePayload = {
          ...submitData,
          changedBy: String(userId),
        };

        await updateStage(stage.stageId, updatePayload);
        toast.success("Stage updated successfully");
      } else {
        // Create new stage
        const createPayload = {
          ...submitData,
          createdBy: String(userId),
        };

        await createStage(createPayload);
        toast.success("Stage created successfully");
      }

      if (onStageSaved) {
        onStageSaved();
      }
      onHide();
    } catch (error) {
      console.error("Error saving stage:", error);

      // Handle specific error messages
      if (error.errors) {
        const errorMessages = Object.values(error.errors).flat();
        toast.error(errorMessages[0] || "Failed to save stage");
      } else {
        toast.error(error.message || "Failed to save stage");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    try {
      await addStageComment({
        stageId: stage.stageId,
        commentText: commentText,
        orgId: orgId,
        createdBy: userId,
      });

      toast.success("Comment added successfully");
      setCommentText("");
      loadTimeline();
    } catch (err) {
      console.error("Error adding comment:", err);
      toast.error(err.message || "Failed to add comment");
    }
  };

  const loadTimeline = async () => {
    if (!stage?.stageId) return;

    try {
      setLoadingTimeline(true);

      // Fetch both comments and activity logs
      const [comments, activityLogs] = await Promise.all([
        fetchStageComments(stage.stageId).catch(() => []),
        fetchStageTimeline(stage.stageId).catch(() => []),
      ]);

      // Combine and sort by date/time
      let allActivities = [...(comments || []), ...(activityLogs || [])];

      // Sort by created date and time (newest first)
      allActivities.sort((a, b) => {
        const dateA = new Date(`${a.createdOnDate} ${a.createdOnTime}`);
        const dateB = new Date(`${b.createdOnDate} ${b.createdOnTime}`);
        return dateB - dateA;
      });

      setTimeline(allActivities);
    } catch (err) {
      console.error("Error loading timeline:", err);
      setTimeline([]);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case "CREATE":
        return "🎉";
      case "COMMENT":
        return "💬";
      case "LOG":
        return "📝";
      case "SOFT_DELETE":
        return "🗑️";
      case "RESTORE":
        return "🔄";
      default:
        return "📌";
    }
  };

  const getActivityTitle = (item) => {
    switch (item.activityType) {
      case "CREATE":
        return "created this stage";
      case "COMMENT":
        return "commented";
      case "LOG":
        return `changed ${item.fieldName || "field"}`;
      case "SOFT_DELETE":
        return "deleted this stage";
      case "RESTORE":
        return "restored this stage";
      default:
        return item.activityType;
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {stage ? "Edit Stage" : "Create Stage"}
          {stage && !stage.active && (
            <span className="ms-2 badge bg-danger">Inactive</span>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Tabs */}
        <div className="mb-3 d-flex gap-2 border-bottom pb-2">
          <Button
            variant={activeTab === "details" ? "primary" : "outline-secondary"}
            size="sm"
            onClick={() => setActiveTab("details")}
          >
            Stage Details
          </Button>

          {stage && (
            <Button
              variant={
                activeTab === "activity" ? "primary" : "outline-secondary"
              }
              size="sm"
              onClick={() => setActiveTab("activity")}
            >
              Activity Timeline
            </Button>
          )}
        </div>

        {/* Stage Details Tab */}
        {activeTab === "details" && (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Stage Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="stageName"
                    value={formData.stageName}
                    onChange={handleChange}
                    placeholder="Enter stage name"
                    isInvalid={!!errors.stageName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.stageName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="stageDescription"
                    value={formData.stageDescription}
                    onChange={handleChange}
                    placeholder="Enter stage description"
                  />
                </Form.Group>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Start Date & Time *</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="stageStartDateTime"
                    value={formData.stageStartDateTime}
                    onChange={handleChange}
                    isInvalid={!!errors.stageStartDateTime}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.stageStartDateTime}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>End Date & Time *</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="stageEndDateTime"
                    value={formData.stageEndDateTime}
                    onChange={handleChange}
                    isInvalid={!!errors.stageEndDateTime}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.stageEndDateTime}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Stage Type</Form.Label>
                  {stageTypesLoading ? (
                    <Form.Control
                      type="text"
                      value="Loading stage types..."
                      disabled
                    />
                  ) : stageTypesError ? (
                    <Form.Control
                      type="text"
                      value="Error loading stage types"
                      disabled
                      isInvalid
                    />
                  ) : stageTypes.length > 0 ? (
                    <Form.Select
                      name="stageType"
                      value={formData.stageType}
                      onChange={handleChange}
                    >
                      <option value="">-- Select Stage Type --</option>
                      {stageTypes.map((type) => (
                        <option key={type.id} value={type.stageTypeGuid}>
                          {type.stageTypeName}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      name="stageType"
                      value={formData.stageType}
                      onChange={handleChange}
                      placeholder="Enter stage type manually"
                    />
                  )}
                  {stageTypes.length === 0 && !stageTypesLoading && !stageTypesError && (
                    <Form.Text className="text-muted text-warning">
                      No stage types found. You can enter a type manually.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              {/* Show Active status when editing */}
              {stage && (
                <Col md={12} className="mb-3">
                  <Form.Group>
                    <Form.Check
                      type="checkbox"
                      label="Active"
                      name="active"
                      checked={formData.active}
                      onChange={handleChange}
                      disabled={true}
                    />
                    <Form.Text className="text-muted">
                      Stage status (active/inactive) - This can only be changed
                      via delete/restore operations
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}
            </Row>

            <Modal.Footer className="px-0 pb-0">
              <Button variant="secondary" onClick={onHide} disabled={loading}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading
                  ? "Saving..."
                  : stage
                    ? "Update Stage"
                    : "Create Stage"}
              </Button>
            </Modal.Footer>
          </Form>
        )}

        {/* Activity Timeline Tab */}
        {activeTab === "activity" && stage && (
          <div>
            {/* Add Comment Section */}
            <div className="mb-4">
              <Form.Group>
                <Form.Label>Add Comment</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Write a comment about this stage..."
                />
                <Button
                  className="mt-2"
                  onClick={handleAddComment}
                  disabled={!commentText.trim()}
                >
                  Add Comment
                </Button>
              </Form.Group>
            </div>

            <hr />

            <h6 className="mb-3">Activity Timeline</h6>

            {loadingTimeline ? (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-primary" />
                <p className="mt-2 text-muted">Loading activity...</p>
              </div>
            ) : timeline.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <p>No activity yet</p>
              </div>
            ) : (
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {timeline.map((item, index) => (
                  <div key={index} className="mb-3 p-3 border rounded bg-light">
                    <div className="d-flex align-items-start">
                      <div className="me-2" style={{ fontSize: "20px" }}>
                        {getActivityIcon(item.activityType)}
                      </div>
                      <div className="flex-grow-1">
                        <div className="mb-1">
                          <strong>
                            {item.createdByName || item.createdBy || "System"}
                          </strong>{" "}
                          <span className="text-muted">
                            {getActivityTitle(item)}
                          </span>
                        </div>

                        {item.activityType === "COMMENT" && (
                          <p className="mb-1 mt-2">
                            {item.commentText || item.commentText}
                          </p>
                        )}

                        {item.activityType === "LOG" && item.fieldName && (
                          <div className="mt-2 p-2 bg-white rounded">
                            <small>
                              <strong>Field:</strong> {item.fieldName}
                            </small>
                            <br />
                            <small>
                              <strong>Changed:</strong>{" "}
                              <span className="text-danger">
                                {item.oldValue || "Not set"}
                              </span>
                              {" → "}
                              <span className="text-success">
                                {item.newValue || "Not set"}
                              </span>
                            </small>
                          </div>
                        )}

                        {(item.activityType === "CREATE" ||
                          item.activityType === "SOFT_DELETE" ||
                          item.activityType === "RESTORE") && (
                          <div className="mt-2 p-2 bg-white rounded">
                            {item.commentText && (
                              <small>{item.commentText}</small>
                            )}
                          </div>
                        )}

                        <small className="text-muted d-block mt-2">
                          {item.createdOnDate}{" "}
                          {item.createdOnTime?.toString().substring(0, 8) || ""}
                        </small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Modal.Footer className="px-0 pb-0 mt-3">
              <Button variant="secondary" onClick={onHide}>
                Close
              </Button>
            </Modal.Footer>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default StageModal;


// import React, { useState, useEffect } from "react";
// import { Modal, Button, Form, Row, Col } from "react-bootstrap";
// import { toast } from "react-toastify";
// import {
//   createStage,
//   updateStage,
//   addStageComment,
//   fetchStageTimeline,
//   fetchStageComments,
// } from "../Api/StageMaster";
// import moment from "moment";

// const StageModal = ({
//   show,
//   onHide,
//   stage,
//   orgId,
//   projectId,
//   onStageSaved,
// }) => {
//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const userId = userDetails?.userId || "";

//   const [formData, setFormData] = useState({
//     stageName: "",
//     stageDescription: "",
//     stageProjectBelong: "",
//     orgId: "",
//     stageStartDateTime: "",
//     stageEndDateTime: "",
//     stageType: "",
//     createdBy: "",
//     active: true,
//   });

//   const [activeTab, setActiveTab] = useState("details");
//   const [timeline, setTimeline] = useState([]);
//   const [commentText, setCommentText] = useState("");
//   const [loadingTimeline, setLoadingTimeline] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [errors, setErrors] = useState({});

//   useEffect(() => {
//     if (stage) {
//       // Editing existing stage
//       setFormData({
//         stageName: stage.stageName || "",
//         stageDescription: stage.stageDescription || "",
//         stageProjectBelong: stage.stageProjectBelong || projectId,
//         orgId: stage.orgId || orgId,
//         stageStartDateTime: stage.stageStartDateTime
//           ? moment(stage.stageStartDateTime).format("YYYY-MM-DDTHH:mm")
//           : "",
//         stageEndDateTime: stage.stageEndDateTime
//           ? moment(stage.stageEndDateTime).format("YYYY-MM-DDTHH:mm")
//           : "",
//         stageType: stage.stageType || "",
//         createdBy: stage.createdBy || userId,
//         active: stage.active !== undefined ? stage.active : true,
//       });
//     } else {
//       // Creating new stage
//       setFormData({
//         stageName: "",
//         stageDescription: "",
//         stageProjectBelong: projectId,
//         orgId: String(orgId),
//         stageStartDateTime: "",
//         stageEndDateTime: "",
//         stageType: "",
//         createdBy: userId,
//         active: true,
//       });
//     }

//     // Reset errors and active tab when modal opens
//     setErrors({});
//     setActiveTab("details");
//   }, [stage, projectId, orgId, show]);

//   // Load timeline when stage exists and activity tab is active
//   useEffect(() => {
//     if (stage && show && activeTab === "activity") {
//       loadTimeline();
//     }
//   }, [stage, show, activeTab]);

//   const validateForm = () => {
//     const newErrors = {};

//     if (!formData.stageName.trim()) {
//       newErrors.stageName = "Stage name is required";
//     }

//     if (!formData.stageStartDateTime) {
//       newErrors.stageStartDateTime = "Stage start date is required";
//     }

//     if (!formData.stageEndDateTime) {
//       newErrors.stageEndDateTime = "Stage end date is required";
//     }

//     if (formData.stageStartDateTime && formData.stageEndDateTime) {
//       const startDate = new Date(formData.stageStartDateTime);
//       const endDate = new Date(formData.stageEndDateTime);

//       if (endDate <= startDate) {
//         newErrors.stageEndDateTime = "Stage end date must be after start date";
//       }
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData((prev) => ({
//       ...prev,
//       [name]: type === "checkbox" ? checked : value,
//     }));

//     // Clear error for this field when user starts typing
//     if (errors[name]) {
//       setErrors((prev) => ({
//         ...prev,
//         [name]: "",
//       }));
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!validateForm()) {
//       return;
//     }

//     setLoading(true);
//     try {
//       const submitData = {
//         stageName: formData.stageName,
//         stageDescription: formData.stageDescription,
//         stageProjectBelong: formData.stageProjectBelong,
//         orgId: String(formData.orgId),
//         stageStartDateTime: formData.stageStartDateTime
//           ? new Date(formData.stageStartDateTime).toISOString()
//           : null,
//         stageEndDateTime: formData.stageEndDateTime
//           ? new Date(formData.stageEndDateTime).toISOString()
//           : null,
//         stageType: formData.stageType,
//       };

//       if (stage) {
//         // Update existing stage - wrap in updateDto object
//         const updatePayload = {
//           ...submitData,
//           changedBy: String(userId),
//         };

//         await updateStage(stage.stageId, updatePayload);
//         toast.success("Stage updated successfully");
//       } else {
//         // Create new stage
//         const createPayload = {
//           ...submitData,
//           createdBy: String(userId),
//         };

//         await createStage(createPayload);
//         toast.success("Stage created successfully");
//       }

//       if (onStageSaved) {
//         onStageSaved();
//       }
//       onHide();
//     } catch (error) {
//       console.error("Error saving stage:", error);

//       // Handle specific error messages
//       if (error.errors) {
//         const errorMessages = Object.values(error.errors).flat();
//         toast.error(errorMessages[0] || "Failed to save stage");
//       } else {
//         toast.error(error.message || "Failed to save stage");
//       }
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleAddComment = async () => {
//     if (!commentText.trim()) {
//       toast.error("Please enter a comment");
//       return;
//     }

//     try {
//       await addStageComment({
//         stageId: stage.stageId,
//         commentText: commentText,
//         orgId: orgId,
//         createdBy: userId,
//       });

//       toast.success("Comment added successfully");
//       setCommentText("");
//       loadTimeline();
//     } catch (err) {
//       console.error("Error adding comment:", err);
//       toast.error(err.message || "Failed to add comment");
//     }
//   };

//   const loadTimeline = async () => {
//     if (!stage?.stageId) return;

//     try {
//       setLoadingTimeline(true);

//       // Fetch both comments and activity logs
//       const [comments, activityLogs] = await Promise.all([
//         fetchStageComments(stage.stageId).catch(() => []),
//         fetchStageTimeline(stage.stageId).catch(() => []),
//       ]);

//       // Combine and sort by date/time
//       let allActivities = [...(comments || []), ...(activityLogs || [])];

//       // Sort by created date and time (newest first)
//       allActivities.sort((a, b) => {
//         const dateA = new Date(`${a.createdOnDate} ${a.createdOnTime}`);
//         const dateB = new Date(`${b.createdOnDate} ${b.createdOnTime}`);
//         return dateB - dateA;
//       });

//       setTimeline(allActivities);
//     } catch (err) {
//       console.error("Error loading timeline:", err);
//       setTimeline([]);
//     } finally {
//       setLoadingTimeline(false);
//     }
//   };

//   const getActivityIcon = (activityType) => {
//     switch (activityType) {
//       case "CREATE":
//         return "🎉";
//       case "COMMENT":
//         return "💬";
//       case "LOG":
//         return "📝";
//       case "SOFT_DELETE":
//         return "🗑️";
//       case "RESTORE":
//         return "🔄";
//       default:
//         return "📌";
//     }
//   };

//   const getActivityTitle = (item) => {
//     switch (item.activityType) {
//       case "CREATE":
//         return "created this stage";
//       case "COMMENT":
//         return "commented";
//       case "LOG":
//         return `changed ${item.fieldName || "field"}`;
//       case "SOFT_DELETE":
//         return "deleted this stage";
//       case "RESTORE":
//         return "restored this stage";
//       default:
//         return item.activityType;
//     }
//   };

//   return (
//     <Modal show={show} onHide={onHide} size="lg" centered>
//       <Modal.Header closeButton>
//         <Modal.Title>
//           {stage ? "Edit Stage" : "Create Stage"}
//           {stage && !stage.active && (
//             <span className="ms-2 badge bg-danger">Inactive</span>
//           )}
//         </Modal.Title>
//       </Modal.Header>

//       <Modal.Body>
//         {/* Tabs */}
//         <div className="mb-3 d-flex gap-2 border-bottom pb-2">
//           <Button
//             variant={activeTab === "details" ? "primary" : "outline-secondary"}
//             size="sm"
//             onClick={() => setActiveTab("details")}
//           >
//             Stage Details
//           </Button>

//           {stage && (
//             <Button
//               variant={
//                 activeTab === "activity" ? "primary" : "outline-secondary"
//               }
//               size="sm"
//               onClick={() => setActiveTab("activity")}
//             >
//               Activity Timeline
//             </Button>
//           )}
//         </div>

//         {/* Stage Details Tab */}
//         {activeTab === "details" && (
//           <Form onSubmit={handleSubmit}>
//             <Row>
//               <Col md={12} className="mb-3">
//                 <Form.Group>
//                   <Form.Label>Stage Name *</Form.Label>
//                   <Form.Control
//                     type="text"
//                     name="stageName"
//                     value={formData.stageName}
//                     onChange={handleChange}
//                     placeholder="Enter stage name"
//                     isInvalid={!!errors.stageName}
//                   />
//                   <Form.Control.Feedback type="invalid">
//                     {errors.stageName}
//                   </Form.Control.Feedback>
//                 </Form.Group>
//               </Col>

//               <Col md={12} className="mb-3">
//                 <Form.Group>
//                   <Form.Label>Description</Form.Label>
//                   <Form.Control
//                     as="textarea"
//                     rows={3}
//                     name="stageDescription"
//                     value={formData.stageDescription}
//                     onChange={handleChange}
//                     placeholder="Enter stage description"
//                   />
//                 </Form.Group>
//               </Col>

//               <Col md={6} className="mb-3">
//                 <Form.Group>
//                   <Form.Label>Start Date & Time *</Form.Label>
//                   <Form.Control
//                     type="datetime-local"
//                     name="stageStartDateTime"
//                     value={formData.stageStartDateTime}
//                     onChange={handleChange}
//                     isInvalid={!!errors.stageStartDateTime}
//                   />
//                   <Form.Control.Feedback type="invalid">
//                     {errors.stageStartDateTime}
//                   </Form.Control.Feedback>
//                 </Form.Group>
//               </Col>

//               <Col md={6} className="mb-3">
//                 <Form.Group>
//                   <Form.Label>End Date & Time *</Form.Label>
//                   <Form.Control
//                     type="datetime-local"
//                     name="stageEndDateTime"
//                     value={formData.stageEndDateTime}
//                     onChange={handleChange}
//                     isInvalid={!!errors.stageEndDateTime}
//                   />
//                   <Form.Control.Feedback type="invalid">
//                     {errors.stageEndDateTime}
//                   </Form.Control.Feedback>
//                 </Form.Group>
//               </Col>

//               <Col md={12} className="mb-3">
//                 <Form.Group>
//                   <Form.Label>Stage Type</Form.Label>
//                   <Form.Select
//                     name="stageType"
//                     value={formData.stageType}
//                     onChange={handleChange}
//                   >
//                     <option value="">-- Select Stage Type --</option>
//                     <option value="Initiation">Initiation</option>
//                     <option value="Planning">Planning</option>
//                     <option value="Design">Design</option>
//                     <option value="Development">Development</option>
//                     <option value="Testing">Testing</option>
//                     <option value="Deployment">Deployment</option>
//                     <option value="Support">Support</option>
//                     <option value="Maintenance">Maintenance</option>
//                     <option value="Closure">Closure</option>
//                   </Form.Select>
//                 </Form.Group>
//               </Col>

//               {/* Show Active status when editing */}
//               {stage && (
//                 <Col md={12} className="mb-3">
//                   <Form.Group>
//                     <Form.Check
//                       type="checkbox"
//                       label="Active"
//                       name="active"
//                       checked={formData.active}
//                       onChange={handleChange}
//                       disabled={true}
//                     />
//                     <Form.Text className="text-muted">
//                       Stage status (active/inactive) - This can only be changed
//                       via delete/restore operations
//                     </Form.Text>
//                   </Form.Group>
//                 </Col>
//               )}
//             </Row>

//             <Modal.Footer className="px-0 pb-0">
//               <Button variant="secondary" onClick={onHide} disabled={loading}>
//                 Cancel
//               </Button>
//               <Button variant="primary" type="submit" disabled={loading}>
//                 {loading
//                   ? "Saving..."
//                   : stage
//                     ? "Update Stage"
//                     : "Create Stage"}
//               </Button>
//             </Modal.Footer>
//           </Form>
//         )}

//         {/* Activity Timeline Tab */}
//         {activeTab === "activity" && stage && (
//           <div>
//             {/* Add Comment Section */}
//             <div className="mb-4">
//               <Form.Group>
//                 <Form.Label>Add Comment</Form.Label>
//                 <Form.Control
//                   as="textarea"
//                   rows={3}
//                   value={commentText}
//                   onChange={(e) => setCommentText(e.target.value)}
//                   placeholder="Write a comment about this stage..."
//                 />
//                 <Button
//                   className="mt-2"
//                   onClick={handleAddComment}
//                   disabled={!commentText.trim()}
//                 >
//                   Add Comment
//                 </Button>
//               </Form.Group>
//             </div>

//             <hr />

//             <h6 className="mb-3">Activity Timeline</h6>

//             {loadingTimeline ? (
//               <div className="text-center py-4">
//                 <div className="spinner-border spinner-border-sm text-primary" />
//                 <p className="mt-2 text-muted">Loading activity...</p>
//               </div>
//             ) : timeline.length === 0 ? (
//               <div className="text-center py-4 text-muted">
//                 <p>No activity yet</p>
//               </div>
//             ) : (
//               <div style={{ maxHeight: "400px", overflowY: "auto" }}>
//                 {timeline.map((item, index) => (
//                   <div key={index} className="mb-3 p-3 border rounded bg-light">
//                     <div className="d-flex align-items-start">
//                       <div className="me-2" style={{ fontSize: "20px" }}>
//                         {getActivityIcon(item.activityType)}
//                       </div>
//                       <div className="flex-grow-1">
//                         <div className="mb-1">
//                           <strong>
//                             {item.createdByName || item.createdBy || "System"}
//                           </strong>{" "}
//                           <span className="text-muted">
//                             {getActivityTitle(item)}
//                           </span>
//                         </div>

//                         {item.activityType === "COMMENT" && (
//                           <p className="mb-1 mt-2">
//                             {item.commentText || item.commentText}
//                           </p>
//                         )}

//                         {item.activityType === "LOG" && item.fieldName && (
//                           <div className="mt-2 p-2 bg-white rounded">
//                             <small>
//                               <strong>Field:</strong> {item.fieldName}
//                             </small>
//                             <br />
//                             <small>
//                               <strong>Changed:</strong>{" "}
//                               <span className="text-danger">
//                                 {item.oldValue || "Not set"}
//                               </span>
//                               {" → "}
//                               <span className="text-success">
//                                 {item.newValue || "Not set"}
//                               </span>
//                             </small>
//                           </div>
//                         )}

//                         {(item.activityType === "CREATE" ||
//                           item.activityType === "SOFT_DELETE" ||
//                           item.activityType === "RESTORE") && (
//                           <div className="mt-2 p-2 bg-white rounded">
//                             {item.commentText && (
//                               <small>{item.commentText}</small>
//                             )}
//                           </div>
//                         )}

//                         <small className="text-muted d-block mt-2">
//                           {item.createdOnDate}{" "}
//                           {item.createdOnTime?.toString().substring(0, 8) || ""}
//                         </small>
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}

//             <Modal.Footer className="px-0 pb-0 mt-3">
//               <Button variant="secondary" onClick={onHide}>
//                 Close
//               </Button>
//             </Modal.Footer>
//           </div>
//         )}
//       </Modal.Body>
//     </Modal>
//   );
// };

// export default StageModal;
